import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

export function useAIProfile(userId?: string) {
  const [data, setData] = useState<{
    ai_profile_description: string | null
    ai_profile_summary: string | null
    ai_profile_interests: string | null
    ai_profile_talents: string | null
    ai_profile_personality: string | null
    ai_profile_study_process: string | null
    ai_profile_updated_at: string | null
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(!!userId);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from("profiles")
      .select("ai_profile_description, ai_profile_summary, ai_profile_interests, ai_profile_talents, ai_profile_personality, ai_profile_study_process, ai_profile_updated_at")
      .eq("user_id", userId)
      .single();
    if (error) setError(error.message);
    setData(
      data ?? {
        ai_profile_description: null,
        ai_profile_summary: null,
        ai_profile_interests: null,
        ai_profile_talents: null,
        ai_profile_personality: null,
        ai_profile_study_process: null,
        ai_profile_updated_at: null,
      }
    );
    setLoading(false);
  };

  // initial fetch
  useEffect(() => {
    if (!userId) return;
    // let cancelled = false;
    (async () => {
      await fetchProfile();
    })();
    return () => { /* cancelled = true; */ };
  }, [userId]);

  // realtime subscription (table update pushes)
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel("profile-ai-updates")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "profiles", filter: `user_id=eq.${userId}` },
        (payload) => {
          const newRow = payload.new as any;
          setData((prev) => ({
            ai_profile_description: newRow.ai_profile_description ?? prev?.ai_profile_description ?? null,
            ai_profile_summary: newRow.ai_profile_summary ?? prev?.ai_profile_summary ?? null,
            ai_profile_interests: newRow.ai_profile_interests ?? prev?.ai_profile_interests ?? null,
            ai_profile_talents: newRow.ai_profile_talents ?? prev?.ai_profile_talents ?? null,
            ai_profile_personality: newRow.ai_profile_personality ?? prev?.ai_profile_personality ?? null,
            ai_profile_study_process: newRow.ai_profile_study_process ?? prev?.ai_profile_study_process ?? null,
            ai_profile_updated_at: newRow.ai_profile_updated_at ?? prev?.ai_profile_updated_at ?? null,
          }));
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId]);

  // polling fallback (in case Realtime not enabled on the table)
  useEffect(() => {
    if (!userId) return;
    const id = setInterval(async () => {
      const { data } = await supabase
        .from("profiles")
        .select("ai_profile_description, ai_profile_summary, ai_profile_interests, ai_profile_talents, ai_profile_personality, ai_profile_study_process, ai_profile_updated_at")
        .eq("user_id", userId)
        .single();
      if (data) setData(data as any);
    }, 4000);
    return () => clearInterval(id);
  }, [userId]);

  return { data, loading, error, refetch: fetchProfile };
}